/**
 * Movement and วิชาตัวเบา on screen: which frame a figure shows while it jumps, flips, steps off the
 * air, glides, runs up a wall or across water, dashes, lands, stops, rides the flying sword, or puts
 * its weapon away on the move. Every one is a `MOTIONS` motion (render/sprites.ts) — key poses with
 * whole skeletons blended between — followed by the simulation's own numbers (height, vertical
 * speed, the dash's time left), so the body moves with the fighter instead of switching stills.
 *
 * The renderer asks here first (renderer.ts `frameOf`); null means "not mine, carry on". Reads
 * world state only (the qinggong state for the player); it keeps a little memory per figure of what
 * it was doing last frame, to start a stop, a glide or a settle at the right moment.
 */
import { COMBAT, MOUNTS, MOVEMENT, type Entity, type QinggongState } from '@nine/shared';
import { FRAMES, type MotionId, type Pose, type SpriteSet } from './sprites';

/** What the renderer draws: an image and how to place it (see renderer.ts `Shot`). */
export interface MoveShot {
  img: HTMLCanvasElement;
  dx?: number;
  dy?: number;
  rot?: number;
  pivot?: number;
}

/** What a figure's frame is chosen from. */
export interface MoveCtx {
  e: Entity;
  set: SpriteSet;
  /** World time (s). */
  t: number;
  facing: number;
  /** Frame `u` (0–1) of a motion, or undefined for figures not drawn with the rig. */
  motion: (id: MotionId, u: number) => HTMLCanvasElement | undefined;
  /** Any pose of the rig (undefined without it). */
  pose: (pose: Pose, ph?: number) => HTMLCanvasElement | undefined;
  /** When it last stepped off the air and when it last landed (effects' anim state). */
  flipAt: number;
  landAt: number;
  /** The player's qinggong (water, glide), or null for anyone else. */
  qg: QinggongState | null;
  /** Holding the fighting stance (in a fight) rather than standing at ease. */
  ready: boolean;
}

/** Seconds each piece takes. */
export const MOVE_TIME = {
  takeoff: 0.16,
  flip: 0.36,
  airStep: 0.26,
  land: 0.2,
  landRun: 0.12,
  dashSettle: 0.14,
  settle: 0.22,
  stop: 0.16,
  glideOpen: 0.2,
} as const;

/** Height above the ground (px) where the legs start reaching down for it. */
const REACH_HEIGHT = 26;
/** Loops a second: skimming strides, wall steps, the rider's rocking. */
const SKIM_RATE = 1.5;
const CLIMB_RATE = 2.6;
const RIDE_RATE = 0.45;

const smooth = (k: number) => {
  const c = Math.max(0, Math.min(1, k));
  return c * c * (3 - 2 * c);
};

/** Strides a figure takes a second (the walk's frame rate; a run is 1.35 times it). */
export function strideFps(e: Entity, set: SpriteSet): number {
  // Figures stride at an unhurried pace that quickens only gently with speed (a little under one
  // full stride, two long steps, per second at the player's speed); beasts keep a gait tied to it.
  return set.poses.cut
    ? Math.max(5, Math.min(16, (3.5 * Math.sqrt(e.speed) * set.walk.length) / 12))
    : (Math.max(6, Math.min(24, e.speed * 2.2)) * set.walk.length) / 4;
}

/** The walk (or run) frame a moving figure is on. */
export const strideFrame = (e: Entity, set: SpriteSet): number =>
  Math.abs(Math.floor(e.animT * strideFps(e, set) * (e.sprinting && set.sprint ? 1.35 : 1))) % FRAMES;

interface Track {
  moving: boolean;
  /** Walk frame last shown, and when it stopped walking (−9: long ago). */
  frame: number;
  stopAt: number;
  dashing: boolean;
  dashEndAt: number;
  gliding: boolean;
  glideAt: number;
  seen: number;
}

export class MoveAnim {
  private readonly tracks = new Map<number, Track>();
  private pruned = 0;

  private track(e: Entity, t: number): Track {
    let k = this.tracks.get(e.id);
    if (!k || t - k.seen > 0.25) {
      k = {
        moving: e.moving,
        frame: 0,
        stopAt: -9,
        dashing: e.dashT > 0,
        dashEndAt: -9,
        gliding: false,
        glideAt: -9,
        seen: t,
      };
      this.tracks.set(e.id, k);
    }
    return k;
  }

  /** Notes what changed since the last frame; call once a frame per figure before asking. */
  observe(c: MoveCtx): void {
    const { e, t, set } = c;
    if (t - this.pruned > 5) this.prune(t);
    const k = this.track(e, t);
    const grounded = e.z <= 0.5;
    if (e.moving) k.frame = strideFrame(e, set);
    if (k.moving && !e.moving && grounded) k.stopAt = t;
    if (e.moving) k.stopAt = -9;
    if (k.dashing && e.dashT <= 0) k.dashEndAt = t;
    const gliding = !!c.qg?.gliding && e.z > 0.5;
    if (gliding && !k.gliding) k.glideAt = t;
    k.moving = e.moving;
    k.dashing = e.dashT > 0;
    k.gliding = gliding;
    k.seen = t;
  }

  /** Forgets figures no longer drawn. */
  private prune(t: number): void {
    this.pruned = t;
    for (const [id, k] of this.tracks) if (t - k.seen > 5) this.tracks.delete(id);
  }

  /** Riding the flying sword: rocking heel to toe on the blade, the body swaying to keep the balance. */
  ride(c: MoveCtx): MoveShot | null {
    const { e, t } = c;
    if (!e.mount || !MOUNTS[e.mount]?.fly) return null;
    const u = ((((t + e.id * 0.37) * RIDE_RATE) % 1) + 1) % 1;
    const img = c.motion('ride', u);
    if (!img) return null;
    return { img, rot: c.facing * Math.sin((t + e.id) * 1.7) * 0.03, pivot: 0 };
  }

  /**
   * In the air: the take-off, the arc (rise, the tuck at the top, the fall, the legs reaching for the
   * ground), a somersault on the second jump, a stamp on the cloud on the third, the glide, and
   * running up a wall.
   */
  air(c: MoveCtx): MoveShot | null {
    const { e, t, set, facing } = c;
    if (e.z <= 0.5) return null;
    const pivot = -set.h * 0.5;
    const lean = facing * (e.moving ? 1 : 0.5);
    // Running up a wall: quick high steps up the face.
    if (e.climbing) {
      const img = c.motion('climb', (((t * CLIMB_RATE) % 1) + 1) % 1);
      if (img) return { img };
    }
    // ร่อนลม: opening the arms out of the fall, then drifting, rocking gently on the air.
    if (c.qg?.gliding) {
      const k = this.track(e, t);
      const img = c.motion('glideOpen', (t - k.glideAt) / MOVE_TIME.glideOpen);
      if (img)
        return {
          img,
          rot: facing * (0.1 + Math.sin(t * 2.4) * 0.04),
          pivot,
          dy: Math.round(Math.sin(t * 3) * 1),
        };
    }
    // Where in the arc: rising until the vertical speed drops, tucked at the top, falling, then
    // (low over the ground on the way down) the legs reaching for it.
    const uv = Math.max(0, Math.min(1, (45 - e.vz) / 90));
    const ua = uv < 1 ? (uv * 2) / 3 : 2 / 3 + Math.max(0, 1 - e.z / REACH_HEIGHT) / 3;
    const arcRot = lean * (0.12 - 0.17 * Math.min(1, uv));
    // A step off the air: the second jump somersaults forward from the tuck into the arc; the third
    // (and any after) stamps down on a cloud and drives up off it.
    const kick = t - c.flipAt;
    if (e.jumps >= 3 && kick < MOVE_TIME.airStep) {
      const img = c.motion('airStep', kick / MOVE_TIME.airStep);
      if (img) return { img, rot: lean * 0.08, pivot };
    }
    if (e.jumps === 2 && kick < MOVE_TIME.flip) {
      const s = smooth(kick / MOVE_TIME.flip);
      const img = c.motion('air', 1 / 3 + (ua - 1 / 3) * s);
      if (img) return { img, rot: facing * Math.PI * 2 * s + arcRot * s, pivot };
    }
    // The take-off from the ground: the crouch, then the push up through the legs. The feet are held
    // on the ground through the crouch and catch up with the body as it springs.
    if (e.jumps === 1 && e.vz > 0) {
      const since = (MOVEMENT.jumpVelocity - e.vz) / MOVEMENT.gravity;
      if (since >= 0 && since < MOVE_TIME.takeoff) {
        const k = since / MOVE_TIME.takeoff;
        const img = c.motion('takeoff', k);
        if (img) return { img, dy: Math.round(e.z * (1 - smooth(k))), rot: lean * 0.12 * k, pivot };
      }
    }
    const img = c.motion('air', ua);
    if (img) return { img, rot: arcRot, pivot };
    // Figures without the rig: the stills.
    const P = set.poses;
    const still = e.vz > 45 ? P.rise : e.vz < -45 ? P.fall : undefined;
    return { img: still ?? set.jump, rot: arcRot, pivot };
  }

  /** The dash: coiling, darting flat out, braking (in the air as on the ground). */
  dash(c: MoveCtx): MoveShot | null {
    const { e, set } = c;
    if (e.dashT <= 0) return null;
    const k = 1 - e.dashT / COMBAT.dashDuration;
    const img = c.motion('dash', k) ?? set.poses.dash ?? set.walk[1];
    return img ? { img } : null;
  }

  /**
   * On the ground just after something: landing (absorbed, then up into the stance, or into the
   * stride), braking out of a dash, and stopping a walk.
   */
  settle(c: MoveCtx): MoveShot | null {
    const { e, t } = c;
    if (e.z > 0.5) return null;
    if (e.cast || e.blocking || e.hitT > 0 || e.swingT > 0 || e.stunT > 0) return null;
    const k = this.track(e, t);
    const since = t - c.landAt;
    if (e.moving && since < MOVE_TIME.landRun) {
      const img = c.motion('landRun', since / MOVE_TIME.landRun);
      if (img) return { img };
    }
    if (!e.moving && since < MOVE_TIME.land) {
      const img = c.motion(c.ready ? 'landReady' : 'land', since / MOVE_TIME.land);
      if (img) return { img };
    }
    const dashed = t - k.dashEndAt;
    if (!e.moving && dashed < MOVE_TIME.dashSettle + (c.ready ? 0 : MOVE_TIME.settle)) {
      // Braking into the fighting stance, then (out of a fight) standing up at ease.
      const img =
        dashed < MOVE_TIME.dashSettle
          ? c.motion('dashSettle', dashed / MOVE_TIME.dashSettle)
          : c.motion('settle', (dashed - MOVE_TIME.dashSettle) / MOVE_TIME.settle);
      if (img) return { img };
    }
    return null;
  }

  /** Just stopped walking: from the stride's nearer step down into the stance at ease. */
  stop(c: MoveCtx): MoveShot | null {
    const { e, t } = c;
    if (e.moving || e.z > 0.5 || c.ready) return null;
    const k = this.track(e, t);
    const u = (t - k.stopAt) / MOVE_TIME.stop;
    if (u >= 1) return null;
    // Step A is the stride's first frame, B its middle: whichever is nearer the frame it stopped on.
    const f = k.frame;
    const toA = Math.min(f, FRAMES - f);
    const toB = Math.abs(f - FRAMES / 2);
    const img = c.motion(toA <= toB ? 'stopA' : 'stopB', Math.max(0, u));
    return img ? { img } : null;
  }

  /** วิ่งบนน้ำ: skimming strides across the water, or balanced on one foot standing on it. */
  water(c: MoveCtx): MoveShot | null {
    const { e, t } = c;
    if (!c.qg?.onWater || e.z > 0.5) return null;
    if (e.cast || e.blocking || e.hitT > 0 || e.swingT > 0 || e.stunT > 0) return null;
    if (e.moving) {
      const img = c.motion('skim', (((e.animT * SKIM_RATE) % 1) + 1) % 1);
      if (img) return { img, dy: -1 };
    }
    const img = c.pose('hover');
    return img ? { img, dy: Math.round(Math.sin(t * 2.2) * 0.6) - 1 } : null;
  }

  /** Putting the weapon away (or drawing it) while walking: the arm through its steps, in stride. */
  stowWalking(c: MoveCtx, since: number, total: number, sheathe: boolean): MoveShot | null {
    const { e, set } = c;
    if (!e.moving || e.sprinting || e.z > 0.5) return null;
    const step = Math.min(FRAMES - 1, Math.floor((since / total) * FRAMES));
    const img = c.pose(sheathe ? 'walkSheathe' : 'walkUnsheathe', step * FRAMES + strideFrame(e, set));
    return img ? { img } : null;
  }
}
